import React, { useState, useEffect, useCallback } from 'react';
import {
    Alert,
    Button,
    Badge,
    Form,
    FormGroup,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Spinner
} from 'reactstrap';
import {
    Autocomplete,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    Stack
} from '@mui/material';
import {
    Clear as ClearIcon,
    Search as SearchIcon,
    Download as DownloadIcon,
    UploadFile as UploadFileIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { AI_UTILITIES } from '../../constants/aiUtilities';
import { get, post, del, put } from '../hooks/apiFetch';

export const UsersTab = () => {
    // ----- Состояния -----
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [infoMessage, setInfoMessage] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);
    const [users, setUsers] = useState([]);
    const [lastUserId, setLastUserId] = useState('');
    const [hasMoreUsers, setHasMoreUsers] = useState(true);

    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isSearchMode, setIsSearchMode] = useState(false);
    const [searchUsers, setSearchUsers] = useState([]);

    const [addUserModal, setAddUserModal] = useState(false);
    const [editUserModal, setEditUserModal] = useState(false);
    const [userForm, setUserForm] = useState(getEmptyUserForm());
    const [currentEditingUser, setCurrentEditingUser] = useState(null);

    const [importUsersModal, setImportUsersModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importProgress, setImportProgress] = useState(null);
    const [importResult, setImportResult] = useState(null);

    const [sortConfig, setSortConfig] = useState({ key: 'surname', direction: 'asc' });

    // Константы
    const availablePermissions = [
        { value: 'aiferma_main_admin_access', label: 'Административная панель', color: 'warning' },
        { value: AI_UTILITIES.TRANSCRIBE.permission, label: 'Транскрибация', color: 'success' },
        { value: AI_UTILITIES.DIARIZE.permission, label: 'Диаризация', color: 'danger' },
        { value: AI_UTILITIES.SUMMARIZE_RESUME.permission, label: 'Резюмирование протокола', color: 'info' },
        { value: AI_UTILITIES.UNIVERSAL_SUMMARIZE.permission, label: 'Универсальная суммаризация', color: 'primary' },
        { value: AI_UTILITIES.DEPERSONALIZE.permission, label: 'Обезличивание данных', color: 'secondary' },
        { value: AI_UTILITIES.RETELLING.permission, label: 'Адаптивное резюмирование', color: 'dark' },
        { value: AI_UTILITIES.STANDARD_CONTROL_PSA.permission, label: 'Нормоконтроль ПСД', color: 'teal' },
        { value: AI_UTILITIES.MARKET_PRICE_ANALYSIS.permission, label: 'Конъюктурный анализ цен', color: 'indigo' },
        { value: AI_UTILITIES.INVOICE_RECOGNIZER.permission, label: 'Распознавание накладных', color: 'purple' },
        { value: "statistics_access", label: 'Доступ к всей статистике', color: 'green' },
        { value: "org_statistics_access", label: 'Доступ к статистике организации', color: 'yellow' },
        { value: AI_UTILITIES.TRANSCRIPT.permission, label: 'Доступ к стенограмме', color: 'success' },
    ];

    // ----- Вспомогательные функции (чистые) -----
    function getEmptyUserForm() {
        return {
            guid: '',
            login: '',
            password: '',
            name: '',
            surname: '',
            patronymic: '',
            post: '',
            organization: '',
            rights: [],
            isActive: true,
            xApiKey: '',
            newRight: ''
        };
    }

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(String(email).toLowerCase());
    };

    const checkPasswordComplexity = (password) => {
        const categories = {
            hasLower: /[a-z]/.test(password),
            hasUpper: /[A-Z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`]/.test(password)
        };
        const passedCategories = Object.values(categories).filter(Boolean).length;
        return passedCategories >= 3;
    };

    const loadOrganizations = useCallback(async () => {
        try {
            setLoadingOrgs(true);

            const data = await get('/api/admin/UserDb/GetOrganizations');
            const orgList = Array.isArray(data)
                ? data.map(org => typeof org === 'string' ? org : (org.name || org.title || org.organization))
                : [];

            
            const uniqueOrgs = [...new Set(orgList)].filter(Boolean);
            setOrganizations(uniqueOrgs);
        } catch (err) {
            console.error('Ошибка загрузки списка организаций:', err);
        } finally {
            setLoadingOrgs(false);
        }
    }, []);

    const validatePassword = (password, email) => {
        if (password.length < 8) {
            return 'Пароль должен содержать минимум 8 символов';
        }
        if (!checkPasswordComplexity(password)) {
            return 'Пароль должен содержать минимум 3 из 4 категорий: большие латинские буквы, маленькие латинские буквы, цифры и спецсимволы';
        }
        if (email) {
            const username = email.split('@')[0].toLowerCase();
            if (password.toLowerCase().includes(username)) {
                return 'Пароль не должен содержать имя (email) пользователя';
            }
        }
        return null;
    };

    const sortUsers = useCallback((usersList, config) => {
        if (!config.key || !usersList || usersList.length === 0) return usersList;
        return [...usersList].sort((a, b) => {
            let aValue = a[config.key] || '';
            let bValue = b[config.key] || '';
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
                if (aValue === '' && bValue !== '') return 1;
                if (aValue !== '' && bValue === '') return -1;
            }
            if (aValue < bValue) return config.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return config.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }, []);

    const getSortIcon = useCallback((key) => {
        if (sortConfig.key !== key) {
            return <span className="material-symbols-outlined sort-icon">unfold_more</span>;
        }
        return sortConfig.direction === 'asc'
            ? <span className="material-symbols-outlined sort-icon">arrow_upward</span>
            : <span className="material-symbols-outlined sort-icon">arrow_downward</span>;
    }, [sortConfig]);

    const formatLastSeen = (lastSeenTime) => {
        if (!lastSeenTime) return '';
        if (lastSeenTime.startsWith('0001-01-01') || lastSeenTime === '0001-01-01T00:00:00') {
            return <Badge className="offline">Не заходил</Badge>;
        }
        const lastSeenDate = new Date(lastSeenTime);
        const now = new Date();
        const diffMs = Math.abs(now - lastSeenDate);
        if (diffMs <= 18000) { // 15 секунд
            return <Badge className="online">Онлайн</Badge>;
        }
        return lastSeenDate.toLocaleString();
    };

    // ----- Функции для работы с правами -----
    const hasPermission = useCallback((permissionValue) => {
        return userForm.rights.includes(permissionValue);
    }, [userForm.rights]);

    const handlePermissionToggle = useCallback((permissionValue) => {
        setUserForm(prev => {
            const currentRights = [...prev.rights];
            const index = currentRights.indexOf(permissionValue);
            if (
                index > -1 &&
                permissionValue === "org_statistics_access" &&
                currentRights.includes("statistics_access")
            ) {
                return prev;
            }

            if (index > -1) {
                currentRights.splice(index, 1);

                if (permissionValue === "statistics_access") {
                    const orgIndex = currentRights.indexOf("org_statistics_access");
                    if (orgIndex > -1) {
                        currentRights.splice(orgIndex, 1);
                    }
                }
            } else {
                currentRights.push(permissionValue);

                if (permissionValue === "statistics_access") {
                    if (!currentRights.includes("org_statistics_access")) {
                        currentRights.push("org_statistics_access");
                    }
                }
            }

            return { ...prev, rights: currentRights };
        });
    }, []);

    // ----- Функции для сообщений -----
    const showError = useCallback((message) => {
        setError(message);
        setInfoMessage(null);
    }, []);

    const showInfo = useCallback((message) => {
        setInfoMessage(message);
        setError(null);
    }, []);

    const clearMessages = useCallback(() => {
        setError(null);
        setInfoMessage(null);
    }, []);

    const loadUsers = useCallback(async () => {
        if (isSearchMode || loading) return;

        setLoading(true);
        clearMessages();
        try {
            const take = 50;
            const newUsers = await get(`/api/UserDb/GetChunk?lastId=${encodeURIComponent(lastUserId)}&take=${encodeURIComponent(take)}`);

            setUsers(prev => {
                const updated = lastUserId === ''
                    ? newUsers
                    : [...prev, ...newUsers];
                return sortUsers(updated, sortConfig);
            });

            if (newUsers.length > 0) {
                setLastUserId(newUsers[newUsers.length - 1].guid);
            }
            setHasMoreUsers(newUsers.length === take);
            setLoading(false);
        } catch (err) {
            setError(err.message || 'Произошла ошибка при загрузке пользователей');
            setLoading(false);
        }
    }, [isSearchMode, lastUserId, sortConfig, clearMessages, sortUsers, loading]);

    const refreshUsersData = useCallback(async () => {
        if (isSearchMode || refreshing) return;
        setRefreshing(true);
        try {
            const take = users.length === 0 ? 50 : users.length;
            const freshUsers = await get(`/api/UserDb/GetChunk?lastId=&take=${encodeURIComponent(take)}`);
            setUsers(prev => {
                const existingMap = new Map(prev.map(u => [u.guid, u]));
                for (const freshUser of freshUsers) {
                    existingMap.set(freshUser.guid, freshUser);
                }
                const merged = Array.from(existingMap.values());
                return sortUsers(merged, sortConfig);
            });
            setRefreshing(false);
        } catch (err) {
            console.error('Ошибка обновления данных:', err);
            setRefreshing(false);
        }
    }, [isSearchMode, refreshing, users.length, sortConfig, sortUsers]);

    const resetUserList = useCallback(() => {
        setUsers([]);
        setLastUserId('');
        setSearchTerm('');
        setSearchUsers([]);
        setIsSearchMode(false);
        setIsSearching(false);
    }, [loadUsers]);

    // ----- Поиск -----
    const performSearch = useCallback(async () => {
        if (!searchTerm.trim()) return;
        setIsSearching(true);
        setIsSearchMode(true);
        setLoading(true);
        setError(null);
        setInfoMessage(null);
        try {
            const encodedTerm = encodeURIComponent(searchTerm.trim());
            const results = await get(`/api/admin/UserDb/SearchUsers?term=${encodedTerm}`);
            const sorted = sortUsers(results, sortConfig);
            setSearchUsers(sorted);
            setIsSearching(false);
            setLoading(false);
        } catch (err) {
            console.error('Search error:', err);
            setError(`Произошла ошибка при поиске: ${err.message}`);
            setIsSearching(false);
            setLoading(false);
            setSearchUsers([]);
        }
    }, [searchTerm, sortConfig, sortUsers]);

    const clearSearchAndReload = useCallback(() => {
        setSearchTerm('');
        setSearchUsers([]);
        setIsSearchMode(false);
        setIsSearching(false);
        setError(null);
        setInfoMessage(null);
        if (users.length === 0) {
            resetUserList();
        }
    }, [users.length, resetUserList]);

    const handleSearchInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setSearchTerm(value);
        setError(null);
        if (name === 'searchTerm' && value.trim() === '' && isSearchMode) {
            clearSearchAndReload();
        }
    }, [isSearchMode, clearSearchAndReload]);

    const handleSearchSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!searchTerm.trim()) {
            clearSearchAndReload();
            return;
        }
        await performSearch();
    }, [searchTerm, clearSearchAndReload, performSearch]);

    // ----- Обработчики сортировки -----
    const handleSort = useCallback((key) => {
        setSortConfig(prev => {
            let direction = 'asc';
            if (prev.key === key && prev.direction === 'asc') {
                direction = 'desc';
            }
            const newConfig = { key, direction };
            // Применяем сортировку к текущему списку
            if (isSearchMode) {
                setSearchUsers(prevUsers => sortUsers(prevUsers, newConfig));
            } else {
                setUsers(prevUsers => sortUsers(prevUsers, newConfig));
            }
            return newConfig;
        });
    }, [isSearchMode, sortUsers]);

    // ----- Добавление / редактирование пользователя -----
    const addUser = useCallback(async () => {
        try {
            const passwordError = validatePassword(userForm.password, userForm.login);
            if (passwordError) {
                showError(passwordError);
                return;
            }
            const userData = {
                login: userForm.login,
                password: userForm.password,
                name: userForm.name,
                surname: userForm.surname,
                patronymic: userForm.patronymic,
                post: userForm.post,
                organization: userForm.organization,
                rights: userForm.rights,
                isActive: userForm.isActive,
                xApiKey: userForm.xApiKey
            };
            await post('/api/admin/UserDb/Add', [userData]);
            setAddUserModal(false);
            setUserForm(getEmptyUserForm());
            showInfo('Пользователь успешно добавлен');
            resetUserList();
        } catch (err) {
            showError(err.message || 'Не удалось добавить пользователя');
            console.error('Ошибка добавления пользователя:', err);
        }
    }, [userForm, showError, showInfo, resetUserList]);

    const updateUser = useCallback(async () => {
        try {
            if (userForm.password) {
                const passwordError = validatePassword(userForm.password, userForm.login);
                if (passwordError) {
                    showError(passwordError);
                    return;
                }
            }
            const userData = {
                guid: currentEditingUser.guid,
                login: userForm.login,
                password: userForm.password,
                name: userForm.name,
                surname: userForm.surname,
                patronymic: userForm.patronymic,
                post: userForm.post,
                organization: userForm.organization,
                rights: userForm.rights,
                isActive: userForm.isActive,
                xApiKey: userForm.xApiKey
            };
            const data = await put('/api/admin/UserDb/Update', [userData]);
            const isSuccess = data?.isSuccess ?? data?.IsSuccess ?? false;
            const errorMessage = data?.error ?? data?.Error ?? null;
            if (isSuccess) {
                setEditUserModal(false);
                setCurrentEditingUser(null);
                setUserForm(getEmptyUserForm());
                showInfo('Пользователь успешно обновлен');
                resetUserList();
            } else {
                showError(errorMessage || 'Ошибка при обновлении пользователя');
            }
        } catch (err) {
            showError(err.message || 'Не удалось обновить пользователя');
            console.error('Ошибка обновления пользователя:', err);
        }
    }, [userForm, currentEditingUser, showError, showInfo, resetUserList]);

    const deleteUsers = useCallback(async (user) => {
        try {
            await del(`/api/admin/UserDb/Delete/${encodeURIComponent(user.guid)}`);
            resetUserList();
            showInfo('Пользователь успешно удален');
        } catch (err) {
            showError(err.message || 'Не удалось удалить пользователя');
            console.error('Ошибка удаления пользователя:', err);
        }
    }, [resetUserList, showError, showInfo]);

    const downloadExampleFile = useCallback(() => {
        const exampleContent = `Почта,Пароль,Фамилия,Имя,Отчество,Должность,Организация,АктивныйПользователь
admin@example.com,SecurePass123,Ivanov,Ivan,Ivanovich,Инженер,ООО Ромашка,true
user1@example.com,UserPass123,Petrov,Peter,-,Стажер,ООО Ромашка,false`;

        const blob = new Blob([`\uFEFF${exampleContent}`], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'users_import_example.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const importUsersFromFile = useCallback(async () => {
        if (!importFile) {
            showError('Выберите файл для импорта');
            return;
        }
        setImportProgress('Чтение и отправка файла на сервер...');
        setImportResult(null);
        try {
            const formData = new FormData();
            formData.append('file', importFile);

            // Сервер теперь возвращает JSON { added: X, skipped: Y }
            const response = await post('/api/admin/UserDb/AddUsersFromFile', formData);

            setImportProgress(null);
            setImportFile(null);

            const added = response?.added || 0;
            const skipped = response?.skipped || 0;

            let details = '';
            if (skipped > 0) {
                details = `Пропущено пользователей (уже существуют в базе): ${skipped}`;
            }

            setImportResult({
                success: true,
                message: `Импорт завершен. Успешно добавлено: ${added}.`,
                details: details
            });
            resetUserList();
        } catch (err) {
            setImportProgress(null);
            setImportResult({
                success: false,
                message: 'Ошибка при импорте',
                details: err.message || 'Не удалось обработать файл'
            });
            console.error('Ошибка импорта пользователей:', err);
        }
    }, [importFile, showError, resetUserList]);

    const handleFileChange = useCallback((e) => {
        setImportFile(e.target.files[0]);
        setImportResult(null);
    }, []);

    // ----- Открытие модалок -----
    const openAddUserModal = useCallback(() => {
        setAddUserModal(true);
        setUserForm(getEmptyUserForm());
    }, []);

    const openEditUserModal = useCallback((user) => {
        setEditUserModal(true);
        setCurrentEditingUser(user);
        setUserForm({
            guid: user.guid,
            login: user.login,
            password: '',
            name: user.name,
            surname: user.surname,
            patronymic: user.patronymic,
            post: user.post,
            organization: user.organization,
            rights: [...user.rights],
            isActive: user.isActive,
            xApiKey: user.xApiKey || '',
            newRight: ''
        });
    }, []);

    // ----- Обработчик изменения формы -----
    const handleUserFormChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setUserForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    }, []);

    useEffect(() => {
        loadUsers();
        loadOrganizations();
        const interval = setInterval(() => {
            refreshUsersData();
        }, 8000);
        return () => clearInterval(interval);
    }, [loadUsers, refreshUsersData, loadOrganizations]);

    // ----- Рендер вспомогательных частей -----
    const renderSearchPanel = () => {
        return (
            <div className="search-panel p-3 rounded">
                <Form onSubmit={handleSearchSubmit} className="row">
                    <div className="col-md-5">
                        <FormGroup>
                            <TextField
                                fullWidth
                                variant="standard"
                                name="searchTerm"
                                id="searchTerm"
                                value={searchTerm}
                                onChange={handleSearchInputChange}
                                placeholder="Введите текст для поиска..."
                                disabled={isSearching}
                                className="search-input text-input"
                                autoComplete="off"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <>
                                                <IconButton
                                                    aria-label={searchTerm.length > 0 ? "Очистить" : undefined}
                                                    onClick={clearSearchAndReload}
                                                    disabled={!searchTerm.trim() && !isSearchMode}
                                                    edge="end"
                                                >
                                                    <ClearIcon className="search-icon clickable-icon" />
                                                </IconButton>
                                                <IconButton
                                                    aria-label="Найти"
                                                    type="submit"
                                                    disabled={isSearching || !searchTerm.trim()}
                                                    edge="end"
                                                >
                                                    <SearchIcon className="search-icon clickable-icon" />
                                                </IconButton>
                                            </>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </FormGroup>
                    </div>
                </Form>
            </div>
        );
    };

    const renderUserTable = () => {
        const displayUsers = isSearchMode ? searchUsers : users;

        return (
            <div className="table-responsive">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th className="ml-2 text-center align-middle sortable" onClick={() => handleSort('login')}>
                                <div className="header-content">
                                    <span className="text-data header-text">Email</span>
                                    <div className="sort-icon-container">{getSortIcon('login')}</div>
                                </div>
                            </th>
                            <th className="text-center align-middle sortable" onClick={() => handleSort('surname')}>
                                <div className="header-content">
                                    <span className="text-data header-text">Фамилия</span>
                                    <div className="sort-icon-container">{getSortIcon('surname')}</div>
                                </div>
                            </th>
                            <th className="text-center align-middle sortable" onClick={() => handleSort('name')}>
                                <div className="header-content">
                                    <span className="text-data header-text">Имя</span>
                                    <div className="sort-icon-container">{getSortIcon('name')}</div>
                                </div>
                            </th>
                            <th className="text-center align-middle sortable" onClick={() => handleSort('patronymic')}>
                                <div className="header-content">
                                    <span className="text-data header-text">Отчество</span>
                                    <div className="sort-icon-container">{getSortIcon('patronymic')}</div>
                                </div>
                            </th>
                            <th className="text-center align-middle sortable" onClick={() => handleSort('post')}>
                                <div className="header-content">
                                    <span className="text-data header-text">Должность</span>
                                    <div className="sort-icon-container">{getSortIcon('post')}</div>
                                </div>
                            </th>
                            <th className="text-center align-middle sortable" onClick={() => handleSort('organization')}>
                                <div className="header-content">
                                    <span className="text-data header-text">Организация</span>
                                    <div className="sort-icon-container">{getSortIcon('organization')}</div>
                                </div>
                            </th>
                            <th className="text-center align-middle sortable" onClick={() => handleSort('lastSeenTime')}>
                                <div className="header-content">
                                    <span className="text-data header-text">Время последнего посещения</span>
                                    <div className="sort-icon-container">{getSortIcon('lastSeenTime')}</div>
                                </div>
                            </th>
                            <th className="text-center align-middle">Статус</th>
                            <th className="text-center align-middle">Количество прав</th>
                            <th className="text-center align-middle">Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayUsers.length > 0 ? (
                            displayUsers.map(user => (
                                <tr key={user.guid}>
                                    <td className="ml-2 text-center align-middle">{user.login}</td>
                                    <td className="text-center align-middle">{user.surname}</td>
                                    <td className="text-center align-middle">{user.name}</td>
                                    <td className="text-center align-middle">{user.patronymic}</td>
                                    <td className="text-center align-middle">{user.post}</td>
                                    <td className="text-center align-middle">{user.organization}</td>
                                    <td className="text-center align-middle">{formatLastSeen(user.lastSeenTime)}</td>
                                    <td className="text-center align-middle">
                                        <Badge className={`${user.isActive ? 'active' : 'unactive'}`}>
                                            {user.isActive ? 'Активен' : 'Неактивен'}
                                        </Badge>
                                    </td>
                                    <td className="text-center align-middle">
                                        {user.rights?.length || 0}
                                    </td>
                                    <td className="text-center align-middle">
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Tooltip title="Просмотр">
                                                <IconButton onClick={() => openEditUserModal(user)} size="small">
                                                    <span className="material-symbols-outlined view-result-icon">info</span>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Удалить пользователя">
                                                <IconButton
                                                    onClick={() => {
                                                        if (window.confirm(`Вы уверены, что хотите удалить пользователя?`)) {
                                                            deleteUsers(user);
                                                        }
                                                    }}
                                                    size="small"
                                                    color="error"
                                                >
                                                    <span className="material-symbols-outlined delete-icon">delete</span>
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="10" className="text-center py-4">
                                    {isSearching ? (
                                        <>
                                            <Spinner size="sm" className="mr-2" />
                                            Идет поиск...
                                        </>
                                    ) : isSearchMode ? (
                                        'Пользователи по вашему запросу не найдены'
                                    ) : (
                                        'Пользователи не найдены'
                                    )}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {hasMoreUsers && !loading && !isSearchMode && (
                    <div className="text-center my-3">
                        <Button
                            onClick={loadUsers}
                            className="dt1520-button active-dt1520-button view-button footer-button default-button"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Spinner size="sm" className="mr-2" />
                                    Загрузка...
                                </>
                            ) : (
                                `Загрузить еще (${users.length} загружено)`
                            )}
                        </Button>
                    </div>
                )}
            </div>
        );
    };

    const renderAddUserModal = () => {
        const emailValid = validateEmail(userForm.login);
        return (
            <Modal isOpen={addUserModal} toggle={() => setAddUserModal(false)} className="edit-user">
                <ModalHeader toggle={() => setAddUserModal(false)}>Добавить нового пользователя</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="login">Email</Label>
                            <Input
                                type="email"
                                name="login"
                                id="login"
                                value={userForm.login}
                                onChange={handleUserFormChange}
                                required
                                className={`standard-text login-input ${userForm.login && !emailValid ? 'is-invalid' : ''}`}
                            />
                            {userForm.login && !emailValid && (
                                <div className="invalid-feedback">Пожалуйста, введите корректный email</div>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="password">Пароль</Label>
                            <Input
                                type="password"
                                name="password"
                                id="password"
                                className="standard-text"
                                value={userForm.password}
                                onChange={handleUserFormChange}
                                required
                            />
                            <small className="form-text text-muted">
                                Пароль должен содержать минимум 8 символов (латинские буквы, цифры и специальные символы)
                            </small>
                        </FormGroup>
                        <FormGroup>
                            <Label for="surname">Фамилия</Label>
                            <Input
                                type="text"
                                name="surname"
                                id="surname"
                                className="standard-text"
                                value={userForm.surname}
                                onChange={handleUserFormChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="name">Имя</Label>
                            <Input
                                type="text"
                                name="name"
                                id="name"
                                className="standard-text"
                                value={userForm.name}
                                onChange={handleUserFormChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="patronymic">Отчество</Label>
                            <Input
                                type="text"
                                name="patronymic"
                                id="patronymic"
                                className="standard-text"
                                value={userForm.patronymic}
                                onChange={handleUserFormChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="post">Должность</Label>
                            <Input
                                type="text"
                                name="post"
                                id="post"
                                className="standard-text"
                                value={userForm.post}
                                onChange={handleUserFormChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="organization">Организация</Label>
                            <Autocomplete
                                freeSolo
                                id="organization-autocomplete-add"
                                options={organizations}
                                loading={loadingOrgs}
                                value={userForm.organization || ''}
                                onChange={(event, newValue) => {
                                    setUserForm(prev => ({
                                        ...prev,
                                        organization: typeof newValue === 'string' ? newValue : ''
                                    }));
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setUserForm(prev => ({
                                        ...prev,
                                        organization: newInputValue
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        name="organization"
                                        placeholder="Выберите или введите новую организацию"
                                        className="standard-text"
                                        InputProps={{
                                            ...params.InputProps,
                                            className: "standard-text",
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loadingOrgs ? <Spinner size="sm" className="mr-2" /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <small className="form-text text-muted">
                                {loadingOrgs ? 'Загрузка списка...' : 'Начните вводить название или выберите из списка'}
                            </small>
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="checkbox"
                                    name="isActive"
                                    checked={userForm.isActive}
                                    onChange={handleUserFormChange}
                                />{' '}
                                Активен
                            </Label>
                        </FormGroup>
                        <FormGroup>
                            <Label>Права доступа</Label>
                            <div className="p-3 border rounded">
                                <div className="row">
                                    {availablePermissions.map((permission) => (
                                        <div className="col-md-6 col-lg-4 mb-3" key={permission.value}>
                                            <div
                                                className={`permission-item p-2 rounded d-flex align-items-center cursor-pointer ${hasPermission(permission.value) ? 'selected' : ''}`}
                                                onClick={() => handlePermissionToggle(permission.value)}
                                                style={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                    hyphens: 'auto'
                                                }}
                                            >
                                                <Input
                                                    type="checkbox"
                                                    checked={hasPermission(permission.value)}
                                                    onChange={() => { }}
                                                    className="invisible"
                                                />
                                                <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                                    <div className="font-weight-bold" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', lineHeight: '1.3' }}>
                                                        {permission.label}
                                                    </div>
                                                    <small className="text-muted" style={{ fontSize: '11px', display: 'block', wordBreak: 'break-all', overflowWrap: 'break-word', lineHeight: '1.2', marginTop: '2px' }}>
                                                        {permission.value}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {userForm.rights.length > 0 && (
                                    <div className="mt-3">
                                        <Label>Выбранные права:</Label>
                                        <div>
                                            {userForm.rights.map((right, index) => (
                                                <Badge
                                                    key={index}
                                                    className={`mr-1 mb-1 permission ${right.replaceAll('_', '-')}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handlePermissionToggle(right)}
                                                >
                                                    {availablePermissions.find(p => p.value === right)?.label || right}
                                                    <span className="ml-1">×</span>
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button
                        onClick={addUser}
                        disabled={!emailValid}
                        className="dt1520-button active-dt1520-button view-button green-button footer-button green-button"
                    >
                        Сохранить
                    </Button>{' '}
                    <Button
                        onClick={() => setAddUserModal(false)}
                        className="dt1520-button active-dt1520-button view-button footer-button default-button"
                    >
                        Отмена
                    </Button>
                </ModalFooter>
            </Modal>
        );
    };

    const renderEditUserModal = () => {
        const emailValid = validateEmail(userForm.login);
        return (
            <Modal isOpen={editUserModal} toggle={() => setEditUserModal(false)} className="edit-user">
                <ModalHeader toggle={() => setEditUserModal(false)}>Просмотр пользователя</ModalHeader>
                <ModalBody>
                    <Form>
                        <FormGroup>
                            <Label for="login">Email</Label>
                            <Input
                                type="email"
                                name="login"
                                id="login"
                                value={userForm.login}
                                onChange={handleUserFormChange}
                                required
                                className={`standard-text login-input ${userForm.login && !emailValid ? 'is-invalid' : ''}`}
                            />
                            {userForm.login && !emailValid && (
                                <div className="invalid-feedback">Пожалуйста, введите корректный email</div>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="surname">Фамилия</Label>
                            <Input
                                className="standard-text"
                                type="text"
                                name="surname"
                                id="surname"
                                value={userForm.surname}
                                onChange={handleUserFormChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="name">Имя</Label>
                            <Input
                                className="standard-text"
                                type="text"
                                name="name"
                                id="name"
                                value={userForm.name}
                                onChange={handleUserFormChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="patronymic">Отчество</Label>
                            <Input
                                className="standard-text"
                                type="text"
                                name="patronymic"
                                id="patronymic"
                                value={userForm.patronymic}
                                onChange={handleUserFormChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="post">Должность</Label>
                            <Input
                                className="standard-text"
                                type="text"
                                name="post"
                                id="post"
                                value={userForm.post}
                                onChange={handleUserFormChange}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="organization">Организация</Label>
                            <Autocomplete
                                freeSolo
                                id="organization-autocomplete-edit"
                                options={organizations}
                                loading={loadingOrgs}
                                value={userForm.organization || ''}
                                onChange={(event, newValue) => {
                                    setUserForm(prev => ({
                                        ...prev,
                                        organization: typeof newValue === 'string' ? newValue : ''
                                    }));
                                }}
                                onInputChange={(event, newInputValue) => {
                                    setUserForm(prev => ({
                                        ...prev,
                                        organization: newInputValue
                                    }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        name="organization"
                                        placeholder="Выберите или введите новую организацию"
                                        className="standard-text"
                                        InputProps={{
                                            ...params.InputProps,
                                            className: "standard-text",
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loadingOrgs ? <Spinner size="sm" className="mr-2" /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <small className="form-text text-muted">
                                {loadingOrgs ? 'Загрузка списка...' : 'Начните вводить название или выберите из списка'}
                            </small>
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="checkbox"
                                    name="isActive"
                                    checked={userForm.isActive}
                                    onChange={handleUserFormChange}
                                />{' '}
                                Активен
                            </Label>
                        </FormGroup>
                        <FormGroup>
                            <Label>Права доступа</Label>
                            <div className="p-3 border rounded">
                                <div className="row">
                                    {availablePermissions.map((permission) => (
                                        <div className="col-md-6 col-lg-4 mb-3" key={permission.value}>
                                            <div
                                                className={`permission-item p-2 rounded d-flex align-items-center cursor-pointer ${hasPermission(permission.value) ? 'selected' : ''}`}
                                                onClick={() => handlePermissionToggle(permission.value)}
                                                style={{
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'break-word',
                                                    hyphens: 'auto'
                                                }}
                                            >
                                                <Input
                                                    type="checkbox"
                                                    checked={hasPermission(permission.value)}
                                                    onChange={() => { }}
                                                    className="invisible"
                                                />
                                                <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                                    <div className="font-weight-bold" style={{ wordBreak: 'break-word', overflowWrap: 'break-word', lineHeight: '1.3' }}>
                                                        {permission.label}
                                                    </div>
                                                    <small className="text-muted" style={{ fontSize: '11px', display: 'block', wordBreak: 'break-all', overflowWrap: 'break-word', lineHeight: '1.2', marginTop: '2px' }}>
                                                        {permission.value}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {userForm.rights.length > 0 && (
                                    <div className="mt-3">
                                        <Label>Выбранные права:</Label>
                                        <div>
                                            {userForm.rights.map((right, index) => (
                                                <Badge
                                                    key={index}
                                                    className={`mr-1 mb-1 permission ${right.replaceAll('_', '-')}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => handlePermissionToggle(right)}
                                                >
                                                    {availablePermissions.find(p => p.value === right)?.label || right}
                                                    <span className="ml-1">×</span>
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </FormGroup>
                    </Form>
                </ModalBody>
                <ModalFooter>
                    <Button
                        onClick={updateUser}
                        disabled={!emailValid}
                        className="dt1520-button active-dt1520-button view-button green-button footer-button green-button"
                    >
                        Сохранить
                    </Button>{' '}
                    <Button
                        onClick={() => setEditUserModal(false)}
                        className="dt1520-button active-dt1520-button view-button footer-button default-button"
                    >
                        Отмена
                    </Button>
                </ModalFooter>
            </Modal>
        );
    };

    const renderImportUsersModal = () => {
        return (
            <Modal isOpen={importUsersModal} toggle={() => setImportUsersModal(false)} size="lg" className="import-modal">
                <ModalHeader toggle={() => setImportUsersModal(false)}>Импорт пользователей из файла</ModalHeader>
                <ModalBody>
                    <div className="mb-4 p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <h5 className="mb-1">Формат файла</h5>
                                <p className="text-muted mb-0 small">
                                    Файл должен содержать 8 колонок, разделенных запятой.
                                    <br />Используйте <code>-</code> для пустых значений.
                                </p>
                            </div>
                            <Button
                                color="link"
                                size="sm"
                                onClick={downloadExampleFile}
                                className="text-decoration-none p-0 d-flex align-items-center"
                                style={{ color: '#0d6efd', fontSize: '0.9rem' }}
                            >
                                <DownloadIcon sx={{ fontSize: 18, marginRight: '4px' }} />
                                Скачать шаблон
                            </Button>
                        </div>

                        <div className="table-responsive mb-0">
                            <table className="table table-sm table-bordered mb-0 small">
                                <thead>
                                    <tr>
                                        <th>Почта</th>
                                        <th>Пароль</th>
                                        <th>Фамилия</th>
                                        <th>Имя</th>
                                        <th>Отчество</th>
                                        <th>Должность</th>
                                        <th>Организация</th>
                                        <th>Активный</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>admin@example.com</td>
                                        <td>SecurePass123</td>
                                        <td>Ivanov</td>
                                        <td>Ivan</td>
                                        <td>Ivanovich</td>
                                        <td>Инженер</td>
                                        <td>ООО Ромашка</td>
                                        <td>true</td>
                                    </tr>
                                    <tr>
                                        <td>user@example.com</td>
                                        <td>UserPass123</td>
                                        <td>Petrov</td>
                                        <td>Petr</td>
                                        <td>-</td>
                                        <td>Стажер</td>
                                        <td>ООО Ромашка</td>
                                        <td>false</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <FormGroup className="mb-4">
                        <Label className="fw-bold mb-2">Загрузка файла</Label>

                        <div
                            className="p-4 border rounded text-center d-flex flex-column align-items-center justify-content-center"
                            style={{
                                cursor: 'pointer',
                                borderStyle: 'dashed',
                                transition: 'all 0.2s',
                                borderColor: importFile ? '#198754' : '#dee2e6',
                                borderWidth: '2px'
                            }}
                            onClick={() => document.getElementById('importFile').click()}
                        >
                            {importFile ? (
                                <CheckCircleIcon sx={{ fontSize: 40, color: '#198754', marginBottom: '8px' }} />
                            ) : (
                                <UploadFileIcon sx={{ fontSize: 40, color: '#0d6efd', marginBottom: '8px' }} />
                            )}

                            {importFile ? (
                                <div>
                                    <div className="font-weight-bold text-dark">{importFile.name}</div>
                                    <div className="text-muted small">
                                        <span>Размер: {(importFile.size / 1024).toFixed(1)} КБ</span>
                                        <span
                                            className="text-danger"
                                            style={{ cursor: 'pointer', textDecoration: 'underline', marginLeft: '8px' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setImportFile(null);
                                                setImportResult(null);
                                            }}
                                        >
                                            Удалить
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="font-weight-bold text-primary">Нажмите для выбора файла</div>
                                    <div className="text-muted small">Поддерживаются форматы .txt и .csv</div>
                                </div>
                            )}
                        </div>

                        <Input
                            type="file"
                            id="importFile"
                            accept=".txt,.csv"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                        />
                    </FormGroup>

                    {importProgress && (
                        <div className="my-3 p-3 text-center rounded d-flex align-items-center justify-content-center">
                            <Spinner size="sm" color="primary" className="me-2" />
                            <span className="fw-bold">{importProgress}</span>
                        </div>
                    )}

                    {importResult && (
                        <Alert color={importResult.success ? 'success' : 'danger'} className="mt-3 mb-0">
                            <h5 className="alert-heading mb-1">{importResult.message}</h5>
                            {importResult.details && (
                                <div className="mt-2 p-2 bg-white rounded border">
                                    <pre className="mb-0 text-wrap" style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                                        {importResult.details}
                                    </pre>
                                </div>
                            )}
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        onClick={importUsersFromFile}
                        disabled={!importFile || !!importProgress}
                        className="dt1520-button active-dt1520-button view-button green-button footer-button green-button"
                    >
                        Импортировать
                    </Button>
                    <Button
                        onClick={() => {
                            setImportUsersModal(false);
                            setImportFile(null);
                            setImportProgress(null);
                            setImportResult(null);
                        }}
                        className="dt1520-button active-dt1520-button view-button footer-button default-button"
                    >
                        Отмена
                    </Button>
                </ModalFooter>
            </Modal>
        );
    };

    // ----- Основной рендер -----
    if (loading && users.length === 0 && !isSearchMode) {
        return (
            <div className="text-center my-5">
                <Spinner color="primary" />
                <p>Загрузка пользователей...</p>
            </div>
        );
    }

    return (
        <div className="mt-2">
            {error && <Alert color="danger">{error}</Alert>}
            {infoMessage && <Alert color="success">{infoMessage}</Alert>}

            <div className="d-flex align-items-center flex-wrap btn-spacing">
                <div className="flex-grow-1 mb-2 mb-md-0" style={{ minWidth: '200px' }}>
                    {renderSearchPanel()}
                </div>
                <div className="d-flex align-items-center flex-nowrap ml-2">
                    <Button
                        onClick={openAddUserModal}
                        className="dt1520-button active-dt1520-button view-button green-button footer-button mr-2"
                    >
                        Добавить пользователя
                    </Button>
                    <Button
                        onClick={() => setImportUsersModal(true)}
                        className="dt1520-button active-dt1520-button view-button blue-button-with-borders footer-button"
                    >
                        Импорт из файла
                    </Button>
                </div>
            </div>

            {renderUserTable()}
            {renderAddUserModal()}
            {renderEditUserModal()}
            {renderImportUsersModal()}
        </div>
    );
};